import { type CSSProperties, useEffect, useState } from 'react';
import { type FxEvent, useRoom } from '../store/room';
import { Emoji } from '../components/Emoji';
import { seatPosition } from './layout';

/** How long each effect stays on screen (ms). */
const LIFETIME: Partial<Record<FxEvent['kind'], number>> = { gift: 1900, emote: 2200 };

/**
 * One-shot table effects from `room.fx`: a gift flies from the sender's seat to the receiver's (pranks splash on
 * arrival, a kiss gift lands as a kiss) and a sticker floats up over the sender's avatar.
 */
export function FxLayer({ capacity }: { capacity: number }) {
  // Effects that happened while the table was not on screen are not replayed.
  const [since] = useState(() => useRoom.getState().fx.at(-1)?.id ?? 0);
  const fx = useRoom((s) => s.fx);
  const shown = fx.filter((f) => f.id > since && LIFETIME[f.kind] !== undefined);
  return (
    <div className="fx-layer" aria-hidden>
      {shown.map((f) => (
        <Effect key={f.id} fx={f} capacity={capacity} />
      ))}
    </div>
  );
}

function Effect({ fx, capacity }: { fx: FxEvent; capacity: number }) {
  useEffect(() => {
    const id = setTimeout(() => useRoom.getState().consumeFx(fx.id), LIFETIME[fx.kind] ?? 0);
    return () => clearTimeout(id);
  }, [fx]);

  if (fx.kind === 'emote') {
    const at = seatPosition(fx.seat, capacity);
    return (
      <span className="fx-emote" style={{ left: `${at.left}%`, top: `${at.top}%` }}>
        <Emoji e={fx.emoji} />
      </span>
    );
  }
  if (fx.kind !== 'gift') return null;
  const to = seatPosition(fx.toSeat, capacity);
  const from = fx.fromSeat >= 0 ? seatPosition(fx.fromSeat, capacity) : { left: 50, top: 50 };
  const path = {
    '--x0': `${from.left}%`,
    '--y0': `${from.top}%`,
    '--x1': `${to.left}%`,
    '--y1': `${to.top}%`,
  } as CSSProperties;
  return (
    <>
      <span className="fx-gift" style={path} data-effect={fx.effect ?? 'none'}>
        <Emoji e={fx.emoji} />
      </span>
      {fx.effect === 'splat' && (
        <span className="fx-splat" style={{ left: `${to.left}%`, top: `${to.top}%` }}>
          <i>
            <Emoji e="💥" />
          </i>
          <b>
            <Emoji e={fx.emoji} />
          </b>
        </span>
      )}
      {fx.effect === 'kiss' && (
        <span className="fx-landing" style={{ left: `${to.left}%`, top: `${to.top}%` }}>
          <Emoji e="💋" />
        </span>
      )}
    </>
  );
}
